<script setup lang="ts">
import type { ArchiveMonth } from '~/types/blog'
import { normalizeFieldText } from '~/composables/useCustomFields'
import { buildPostPath } from '~/composables/usePermalink'

const route = useRoute()
const blogApi = useBlogApi()
const siteData = await useSiteData()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)
const fields = siteData?.setting?.customFields
const pageSize = 10

const { data: firstPage } = await useAsyncData('archives:first-page', () =>
  blogApi.getArchives({ page: 1, pageSize })
)

const list = ref<ArchiveMonth[]>([])
const total = ref(0)
const nextPage = ref(2)
const loadingMore = ref(false)
const sentinel = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null

const hasMore = computed(() => list.value.length < total.value)

const resetList = () => {
  list.value = firstPage.value?.list || []
  total.value = firstPage.value?.total || 0
  nextPage.value = 2
}

watch(
  () => firstPage.value,
  () => resetList(),
  { immediate: true }
)

const loadMore = async () => {
  if (loadingMore.value || !hasMore.value) {
    return
  }
  loadingMore.value = true
  try {
    const data = await blogApi.getArchives({ page: nextPage.value, pageSize })
    list.value = [...list.value, ...(data.list || [])]
    total.value = data.total || total.value
    nextPage.value += 1
  } finally {
    loadingMore.value = false
  }
}

const bindObserver = () => {
  if (!import.meta.client) return
  observer?.disconnect()
  observer = null
  if (!sentinel.value || !hasMore.value) {
    return
  }
  observer = new IntersectionObserver(
    entries => {
      if (entries[0]?.isIntersecting) {
        void loadMore()
      }
    },
    { rootMargin: '260px 0px' }
  )
  observer.observe(sentinel.value)
}

onMounted(bindObserver)
watch([sentinel, hasMore], () => bindObserver())

onBeforeUnmount(() => {
  observer?.disconnect()
})

const siteName = normalizeFieldText(siteData?.setting?.customFields, 'siteName', 'Yuelai Blog')
const pageDesc = normalizeFieldText(siteData?.setting?.customFields, 'archivesPageDescription', '按时间回溯文章内容。')

useSeoMeta({
  title: `归档 - ${siteName}`,
  description: pageDesc,
  ogTitle: `归档 - ${siteName}`,
  ogDescription: pageDesc
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})
</script>

<template>
  <section class="archives-page">
    <div class="card hero">
      <h1>归档</h1>
      <p>{{ pageDesc }}</p>
    </div>

    <ul v-if="list.length" class="month-list">
      <li v-for="item in list" :key="item.month" class="card month-card">
        <div class="month-row">
          <div>
            <NuxtLink :to="`/archives/${item.month.replace('-', '/')}`">{{ item.month }}</NuxtLink>
            <p class="month-subtitle">共 {{ item.total }} 篇内容</p>
          </div>
          <span>{{ item.total }}</span>
        </div>
        <ul v-if="(item.items || []).length" class="post-list">
          <li v-for="post in (item.items || []).slice(0, 5)" :key="post.id">
            <NuxtLink :to="buildPostPath(fields, { ID: post.id, slug: post.slug })">
              <strong>{{ post.title || post.slug }}</strong>
              <time v-if="post.publishedAt">{{ new Date(post.publishedAt).toLocaleDateString('zh-CN') }}</time>
            </NuxtLink>
          </li>
        </ul>
      </li>
    </ul>

    <template v-if="list.length">
      <div ref="sentinel" class="scroll-sentinel" />
      <div v-if="loadingMore" class="load-state">加载中...</div>
      <div v-else-if="!hasMore" class="load-state">没有更多了</div>
    </template>

    <div v-else class="empty-card">暂无归档</div>
  </section>
</template>

<style scoped>
.archives-page {
  display: grid;
  gap: 14px;
}

.hero {
  padding: 20px;
}

h1 {
  font-size: 34px;
}

.hero p {
  margin-top: 8px;
  color: var(--text-soft);
}

.month-list {
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;
  list-style: none;
}

.month-card {
  padding: 16px 18px;
}

.month-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.month-row a {
  font-size: 22px;
  color: var(--text);
  text-decoration: none;
}

.month-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-soft);
}

.post-list {
  margin: 0;
  padding-left: 16px;
  display: grid;
  gap: 10px;
}

.post-list li {
  min-width: 0;
}

span {
  min-width: 34px;
  height: 34px;
  display: inline-grid;
  place-items: center;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--brand);
  font-size: 13px;
  background: var(--brand-soft);
}

.post-list a {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 12px;
  color: var(--text);
  text-decoration: none;
}

.post-list strong {
  min-width: 0;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  line-height: 1.7;
}

.post-list time {
  flex: none;
  font-size: 12px;
  color: var(--text-soft);
}

.scroll-sentinel {
  width: 100%;
  height: 1px;
}

.load-state {
  padding: 8px 0 2px;
  text-align: center;
  color: var(--text-soft);
  font-size: 13px;
}
</style>
